import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Moon, Save, Ban, ShieldCheck, CalendarClock } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton } from "@/components/BookingShell";

const CH = [
    { key: "email", label: "Email" },
    { key: "sms", label: "SMS" },
    { key: "whatsapp", label: "WhatsApp" },
];

export function CustomerPreferencesPanel({ customerId }) {
    const [prefs, setPrefs] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        staffApi.commsGetPreferences(customerId)
            .then((r) => setPrefs(r.preferences))
            .catch(() => setPrefs(null));
    };
    useEffect(() => { if (customerId) load(); /* eslint-disable-next-line */ }, [customerId]);

    const save = async () => {
        if (!prefs) return;
        setBusy(true);
        try {
            await staffApi.commsUpsertPreferences(customerId, {
                customer_id: Number(customerId),
                email: prefs.email || null,
                phone: prefs.phone || null,
                transactional_email: !!prefs.transactional_email,
                transactional_sms: !!prefs.transactional_sms,
                transactional_whatsapp: !!prefs.transactional_whatsapp,
                marketing_email: !!prefs.marketing_email,
                marketing_sms: !!prefs.marketing_sms,
                marketing_whatsapp: !!prefs.marketing_whatsapp,
                quiet_hours_enabled: !!prefs.quiet_hours_enabled,
                quiet_hours_start: prefs.quiet_hours_start || null,
                quiet_hours_end: prefs.quiet_hours_end || null,
                timezone: prefs.timezone || null,
                unsubscribed_all_marketing: !!prefs.unsubscribed_all_marketing,
                notes: prefs.notes || null,
                marketing_eligibility: prefs.marketing_eligibility || null,
                consent_basis: prefs.consent_basis || null,
                consent_date: prefs.consent_date || null,
                consent_source: prefs.consent_source || null,
                opt_out_date: prefs.opt_out_date || null,
                opt_out_channel: prefs.opt_out_channel || null,
            });
            toast.success("Preferences saved");
            load();
        } catch (e) { toast.error(e.message || "Save failed"); }
        finally { setBusy(false); }
    };

    if (!prefs) return <Skeleton className="h-32" />;
    const setP = (patch) => setPrefs({ ...prefs, ...patch });

    return (
        <div className="rounded-2xl glass p-5 space-y-4" data-testid="cust-comms-preferences">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <BellRing className="h-4 w-4 text-gold" strokeWidth={1.5} />
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Communication preferences</div>
                </div>
                <button onClick={save} disabled={busy} data-testid="prefs-save"
                        className="h-9 px-4 rounded-full bg-gold text-navy font-semibold text-xs inline-flex items-center gap-1 disabled:opacity-40">
                    <Save className="h-3.5 w-3.5" /> Save preferences
                </button>
            </div>

            <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 mb-1.5">Essential / transactional</div>
                <p className="text-[11px] text-white/50 mb-2">Booking confirmations, reminders, consultation forms, aftercare, payment requests.</p>
                <div className="flex flex-wrap gap-2">
                    {CH.map((c) => {
                        const key = `transactional_${c.key}`;
                        const on = !!prefs[key];
                        return (
                            <button key={c.key} onClick={() => setP({ [key]: !on })} data-testid={`prefs-tx-${c.key}`}
                                    className={`h-8 px-3 rounded-full border text-xs transition-colors ${
                                        on ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100" : "border-white/15 text-white/60"
                                    }`}>
                                {c.label}: {on ? "On" : "Off"}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 mb-1.5">Marketing (opt-in)</div>
                <p className="text-[11px] text-white/50 mb-2">Promotions, birthday offers, win-back campaigns. Off by default.</p>
                <div className="flex flex-wrap gap-2">
                    {CH.map((c) => {
                        const key = `marketing_${c.key}`;
                        const on = !!prefs[key];
                        return (
                            <button key={c.key} onClick={() => setP({ [key]: !on })} data-testid={`prefs-mk-${c.key}`}
                                    disabled={prefs.unsubscribed_all_marketing}
                                    className={`h-8 px-3 rounded-full border text-xs transition-colors disabled:opacity-40 ${
                                        on ? "bg-gold/15 border-gold/40 text-gold" : "border-white/15 text-white/60"
                                    }`}>
                                {c.label}: {on ? "On" : "Off"}
                            </button>
                        );
                    })}
                </div>
                <label className="mt-2 inline-flex items-center gap-2 text-xs text-white/70">
                    <input type="checkbox" checked={!!prefs.unsubscribed_all_marketing}
                           onChange={(e) => setP({ unsubscribed_all_marketing: e.target.checked })}
                           data-testid="prefs-unsub-all" className="accent-gold" />
                    <Ban className="h-3 w-3" /> Unsubscribed from all marketing
                </label>

                {/* Consent audit trail — surfaces the legal basis so staff can
                    prove compliance if ever challenged. */}
                {(prefs.marketing_eligibility || prefs.consent_basis || prefs.opt_out_date) && (
                    <div className="mt-3 rounded-lg bg-white/[0.03] border border-white/10 p-3 space-y-1 text-[11px]" data-testid="prefs-consent-audit">
                        <div className="flex items-center gap-1.5 text-gold/85 text-[10px] uppercase tracking-[0.16em] font-semibold">
                            <ShieldCheck className="h-3 w-3" /> Consent audit
                        </div>
                        {prefs.marketing_eligibility && (
                            <div className="text-white/70">
                                Basis:{" "}
                                <span className="text-white">
                                    {prefs.marketing_eligibility === "consent" && "Explicit consent"}
                                    {prefs.marketing_eligibility === "soft_opt_in" && "ICO PECR soft opt-in"}
                                    {prefs.marketing_eligibility === "none" && "No basis recorded"}
                                </span>
                            </div>
                        )}
                        {prefs.consent_basis && (
                            <div className="text-white/55">{prefs.consent_basis}</div>
                        )}
                        {(prefs.consent_date || prefs.consent_source) && (
                            <div className="text-white/55 flex items-center gap-1">
                                <CalendarClock className="h-3 w-3" />
                                {prefs.consent_date && new Date(prefs.consent_date).toLocaleString()}
                                {prefs.consent_source && (
                                    <span className="ml-1">· source: {prefs.consent_source}</span>
                                )}
                            </div>
                        )}
                        {prefs.opt_out_date && (
                            <div className="text-red-300">
                                Opted out on {new Date(prefs.opt_out_date).toLocaleDateString()}
                                {prefs.opt_out_channel && ` (${prefs.opt_out_channel})`}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 mb-1.5 flex items-center gap-1">
                    <Moon className="h-3 w-3" /> Quiet hours
                    <span className="text-white/40 normal-case tracking-normal ml-1">(schema only — not enforced yet)</span>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-white/70">
                    <input type="checkbox" checked={!!prefs.quiet_hours_enabled}
                           onChange={(e) => setP({ quiet_hours_enabled: e.target.checked })}
                           data-testid="prefs-quiet-enabled" className="accent-gold" />
                    Enable quiet hours
                </label>
                {prefs.quiet_hours_enabled && (
                    <div className="mt-2 grid grid-cols-2 gap-3 max-w-sm">
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 mb-1">From</div>
                            <input type="time" value={prefs.quiet_hours_start || ""} onChange={(e) => setP({ quiet_hours_start: e.target.value })}
                                   data-testid="prefs-quiet-start"
                                   className="bg-transparent border-b hairline py-1 text-white text-sm outline-none focus:border-gold" />
                        </label>
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 mb-1">To</div>
                            <input type="time" value={prefs.quiet_hours_end || ""} onChange={(e) => setP({ quiet_hours_end: e.target.value })}
                                   data-testid="prefs-quiet-end"
                                   className="bg-transparent border-b hairline py-1 text-white text-sm outline-none focus:border-gold" />
                        </label>
                    </div>
                )}
            </div>
        </div>
    );
}
